/**
 * offlineSearchResults — most między lokalnymi źródłami notatek a formatem
 * `NormalizedSearchResult`, którego oczekuje maszyna animacji wyszukiwania.
 *
 * Używane przez `useSearchV2`, gdy urządzenie jest offline (lub backend zwraca
 * błąd sieciowy mimo `navigator.onLine === true`) — Edge Search Fallback:
 * zamiast strzelać do `/api/screen1/search-rag`, przeszukujemy notatki
 * zgromadzone lokalnie z DWÓCH źródeł:
 *  1. `engines/offline_search.ts` — indeks nad starym magazynem `offline_notes`
 *     (IndexedDB), zasilanym przez ścieżkę dyktowania E2/E3.
 *  2. Kolejka synchronizacji Dexie (`SyncQueueRepository`) — KAŻDA notatka
 *     (dyktowana i wpisana z klawiatury) trafia tu natychmiast po zapisaniu,
 *     zanim jeszcze dotrze do backendu. To jedyne źródło dla notatek wpisanych
 *     z klawiatury offline (patrz `Screen1.handleTypedNoteSubmit`) — prosty
 *     `.includes()`, bez rankingu.
 */

import { searchOffline } from '../engines/offline_search';
import type { SearchResult } from '../engines/types';
import { db } from '../infrastructure/offline/AppDatabase';
import type { SyncQueueItem } from '../infrastructure/offline/AppDatabase';
import { SyncQueueRepository } from '../infrastructure/repositories/SyncQueueRepository';
import type { NormalizedSearchItem, NormalizedSearchResult } from './SearchBridge';

/** Domyślny limit wyników lokalnych — spójny z parserem zapytań offline. */
const OFFLINE_RESULT_LIMIT = 20;

/** Współdzielona instancja repozytorium kolejki — jak w `Screen1`/`IncidentSummaryScreen`. */
const noteQueueRepository = new SyncQueueRepository(db);

/** Kształt payloadu wpisów `entityType: 'note'` — patrz `Screen1.enqueueNoteOffline`. */
interface QueuedNotePayload {
  content: string;
  capturedAt?: string;
}

function isQueuedNotePayload(payload: unknown): payload is QueuedNotePayload {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    typeof (payload as { content?: unknown }).content === 'string'
  );
}

function toQueuedNoteItem(item: SyncQueueItem, payload: QueuedNotePayload): NormalizedSearchItem {
  return {
    conversationId: item.id ?? 0,
    employeeId: null,
    firstName: '',
    lastName: '',
    position: '',
    shortSummary: payload.content.slice(0, 80),
    longSummary: payload.content,
    createdAt: payload.capturedAt ?? new Date(item.createdAt).toISOString(),
  };
}

/**
 * Proste dopasowanie tekstowe (`.includes`, bez czułości na wielkość liter) nad
 * notatkami z kolejki synchronizacji Dexie — jedynego magazynu, w którym na
 * pewno znajdzie się notatka wpisana z klawiatury offline.
 */
async function searchQueuedNotes(query: string): Promise<NormalizedSearchItem[]> {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];

  const queued = await noteQueueRepository.getByEntityType('note');
  const items: NormalizedSearchItem[] = [];
  for (const item of queued) {
    if (!isQueuedNotePayload(item.payload)) continue;
    if (!item.payload.content.toLowerCase().includes(needle)) continue;
    items.push(toQueuedNoteItem(item, item.payload));
  }
  return items;
}

/** Stabilny, dodatni identyfikator liczbowy z tekstowego id notatki (`note_...`). */
function toNumericId(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

function toNormalizedItem(result: SearchResult): NormalizedSearchItem {
  return {
    conversationId: toNumericId(result.id),
    employeeId: null,
    firstName: '',
    lastName: '',
    position: '',
    shortSummary: result.title || result.shortNote,
    longSummary: result.shortNote,
    createdAt: '',
    similarityScore: result.score,
  };
}

/**
 * Wykonuje wyszukiwanie w lokalnych źródłach notatek (indeks silnika offline +
 * kolejka Dexie) i zwraca wynik w formacie `NormalizedSearchResult`. `ragAnswer`
 * jest zawsze `null` (brak LLM offline), a `searchMeta.fallback` = `true` — UI
 * może zaznaczyć, że to wynik lokalny.
 */
export async function getOfflineNormalizedResults(
  query: string,
): Promise<NormalizedSearchResult> {
  const [engineResults, queuedNoteItems] = await Promise.all([
    searchOffline({ text: query, industry: 'galvo' }),
    searchQueuedNotes(query),
  ]);
  const engineItems = engineResults.slice(0, OFFLINE_RESULT_LIMIT).map(toNormalizedItem);

  // Notatka dyktowana offline trafia RÓWNOCZEŚNIE do starego indeksu E2/E3 i do
  // kolejki Dexie — bez odfiltrowania user widziałby ją dwukrotnie. Notatki z
  // kolejki idą na początek: to one odpowiadają na wpisowy przypadek z tego
  // zgłoszenia (notatka zapisana chwilę temu, jeszcze niezsynchronizowana).
  const seen = new Set(engineItems.map((i) => i.longSummary.trim().toLowerCase()));
  const freshQueueItems = queuedNoteItems.filter(
    (i) => !seen.has(i.longSummary.trim().toLowerCase()),
  );

  const items = [...freshQueueItems, ...engineItems].slice(0, OFFLINE_RESULT_LIMIT);

  return {
    items,
    ragAnswer: null,
    searchMeta: {
      totalFound: items.length,
      parsedDateStart: null,
      parsedDateEnd: null,
      searchConcept: query,
      fallback: true,
    },
  };
}
